import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { ListChecks, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import useIsOrgAdmin from 'hooks/useIsOrgAdmin';
import { selectGroupGuid, useAppSelector } from 'slices/commonSelectors';
import { useAorText } from 'utils/aorTerminology';
import {
  ONBOARDING_HREF,
  ONBOARDING_PARAM,
  shouldShowSetupBanner,
} from './onboardingModel';
import { useOrgOnboardingProgress } from './useOrgOnboarding';

const DISMISS_KEY_PREFIX = 'pratus.onboardingBannerDismissed.';

function readDismissed(groupGuid: string | undefined): boolean {
  if (!groupGuid) return false;
  try {
    return window.localStorage.getItem(DISMISS_KEY_PREFIX + groupGuid) === '1';
  } catch {
    return false;
  }
}

/** One-time nudge for admins of an org with no AORs that never opened setup. */
export function OnboardingBanner() {
  const groupGuid = useAppSelector(selectGroupGuid);
  const isAdmin = useIsOrgAdmin();
  const aorText = useAorText();
  const [searchParams] = useSearchParams();
  const { state, counts, aorsLoaded } = useOrgOnboardingProgress({
    enabled: isAdmin,
  });
  const [dismissedFor, setDismissedFor] = useState<string | null>(null);
  const dismissed = dismissedFor === groupGuid || readDismissed(groupGuid);

  if (
    searchParams.has(ONBOARDING_PARAM) ||
    !shouldShowSetupBanner({
      isAdmin,
      aorCount: aorsLoaded ? counts.aors : null,
      state,
      dismissed,
    })
  ) {
    return null;
  }

  const dismiss = () => {
    if (!groupGuid) return;
    try {
      window.localStorage.setItem(DISMISS_KEY_PREFIX + groupGuid, '1');
    } catch {
      // Private mode: dismissal lasts for this page only.
    }
    setDismissedFor(groupGuid);
  };

  return (
    <div
      role="status"
      className="dark tw:fixed tw:bottom-4 tw:left-4 tw:z-40 tw:flex tw:max-w-sm tw:items-start tw:gap-3 tw:rounded-lg tw:border tw:border-border tw:bg-background tw:p-3 tw:text-foreground tw:shadow-lg"
    >
      <ListChecks className="tw:mt-0.5 tw:size-4 tw:shrink-0 tw:text-primary" />
      <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-2">
        <div>
          <p className="tw:m-0 tw:text-sm tw:font-medium">
            Set up your organization
          </p>
          <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
            {aorText(
              'Define your AORs, import assets, and add automation rules in a few steps.'
            )}
          </p>
        </div>
        <div>
          <Button
            size="sm"
            nativeButton={false}
            render={<Link to={ONBOARDING_HREF} />}
          >
            Start setup
          </Button>
        </div>
      </div>
      <Button
        type="button"
        variant="ghost"
        size="icon-xs"
        aria-label="Dismiss"
        onClick={dismiss}
      >
        <X />
      </Button>
    </div>
  );
}
